import { useCallback, useMemo, useState } from 'react';

import { useQuery } from 'react-query';
import {
  getHistoricalProfitability,
  HistoricalProfitabilityRequestItem,
  HistoricalProfitabilityResponse,
} from 'src/services/fidcComparator';

import { ProfitabilityPeriodKey, SelectedFund } from './types';

export function useHistoricalProfitability(
  selectedFunds: SelectedFund[] | undefined,
  baseMonth: string | undefined,
) {
  const [classSeriSelections, setClassSeriSelections] = useState<
    Record<string, string[]>
  >({});

  const handleSelectionsChange = useCallback(
    (selections: Record<string, string[]>) => {
      setClassSeriSelections(selections);
    },
    [],
  );

  const requestItems = useMemo<HistoricalProfitabilityRequestItem[]>(() => {
    if (!selectedFunds) return [];
    return selectedFunds
      .filter(
        (f) => (classSeriSelections[String(f.operationId)]?.length ?? 0) > 0,
      )
      .map((f) => ({
        operationId: f.operationId,
        classSeries: classSeriSelections[String(f.operationId)],
      }));
  }, [selectedFunds, classSeriSelections]);

  const requestItemsKey = requestItems
    .map((i) => `${i.operationId}:[${i.classSeries.join('|')}]`)
    .join(',');

  const {
    data: profitabilityData,
    isLoading: isProfitabilityLoading,
    isError: isProfitabilityError,
  } = useQuery<HistoricalProfitabilityResponse>(
    ['historical-profitability', baseMonth, requestItemsKey],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    () => getHistoricalProfitability(baseMonth!, requestItems),
    {
      enabled: Boolean(baseMonth) && requestItems.length > 0,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const [accumulatedPeriod, setAccumulatedPeriod] =
    useState<ProfitabilityPeriodKey>('accumulated');
  const [monthlyReturnsPeriod, setMonthlyReturnsPeriod] =
    useState<ProfitabilityPeriodKey>('accumulated');
  const [monthlyCdiPeriod, setMonthlyCdiPeriod] =
    useState<ProfitabilityPeriodKey>('accumulated');

  const uniqueFunds = useMemo(() => {
    const summary =
      (profitabilityData?.accumulated ?? profitabilityData?.month)?.summary ??
      [];
    return [...summary].sort((a, b) => b.rentability - a.rentability);
  }, [profitabilityData]);

  const noDataFunds = useMemo(() => {
    if (!profitabilityData || requestItems.length === 0) return [];
    const available = new Set(
      uniqueFunds.map((f) => `${f.operationId}::${f.classSeries}`),
    );
    return requestItems.flatMap((item) =>
      item.classSeries
        .filter((cs) => !available.has(`${item.operationId}::${cs}`))
        .map((cs) => {
          const fund = selectedFunds?.find(
            (f) => String(f.operationId) === String(item.operationId),
          );
          return `${fund?.name ?? item.operationId} | ${cs}`;
        }),
    );
  }, [profitabilityData, requestItems, uniqueFunds, selectedFunds]);

  const accumulatedSeries = useMemo(() => {
    const periodData = profitabilityData?.[accumulatedPeriod];
    if (!periodData) return [];

    const fundSeries = uniqueFunds.map((fund) => {
      let multiplier = 1;
      const seriesData = periodData.data.map((point) => {
        const item = point.items.find(
          (i) =>
            String(i.operationId) === String(fund.operationId) &&
            i.classSeries === fund.classSeries,
        );
        if (item != null) {
          multiplier *= 1 + item.rentability / 100;
          return { x: point.date, y: (multiplier - 1) * 100 };
        }
        return { x: point.date, y: null };
      });
      return { name: `${fund.name} | ${fund.classSeries}`, data: seriesData };
    });

    const rawCdi = periodData.data[0]?.items[0]?.cdi;
    const cdiNeedsScale = rawCdi != null && rawCdi < 0.5;
    let cdiMultiplier = 1;
    const cdiSeries = {
      name: 'CDI',
      data: periodData.data.map((point) => {
        const v = point.items[0]?.cdi ?? null;
        if (v == null) return { x: point.date, y: null };
        const pct = cdiNeedsScale ? v * 100 : v;
        cdiMultiplier *= 1 + pct / 100;
        return { x: point.date, y: (cdiMultiplier - 1) * 100 };
      }),
    };

    return [...fundSeries, cdiSeries];
  }, [profitabilityData, accumulatedPeriod, uniqueFunds]);

  const monthlyReturnsSeries = useMemo(() => {
    const periodData = profitabilityData?.[monthlyReturnsPeriod];
    if (!periodData) return [];

    const rawCdi = periodData.data[0]?.items[0]?.cdi;
    const cdiNeedsScale = rawCdi != null && rawCdi < 0.5;

    const fundSeries = uniqueFunds.map((fund) => ({
      name: `${fund.name} | ${fund.classSeries}`,
      data: periodData.data.map((point) => {
        const item = point.items.find(
          (i) =>
            String(i.operationId) === String(fund.operationId) &&
            i.classSeries === fund.classSeries,
        );
        return { x: point.date, y: item?.rentability ?? null };
      }),
    }));

    const cdiSeries = {
      name: 'CDI',
      data: periodData.data.map((point) => {
        const v = point.items[0]?.cdi ?? null;
        return {
          x: point.date,
          y: v != null ? (cdiNeedsScale ? v * 100 : v) : null,
        };
      }),
    };

    return [...fundSeries, cdiSeries];
  }, [profitabilityData, monthlyReturnsPeriod, uniqueFunds]);

  const monthlyCdiSeries = useMemo(() => {
    const periodData = profitabilityData?.[monthlyCdiPeriod];
    if (!periodData) return [];

    return uniqueFunds.map((fund) => ({
      name: `${fund.name} | ${fund.classSeries}`,
      data: periodData.data.map((point) => {
        const item = point.items.find(
          (i) =>
            String(i.operationId) === String(fund.operationId) &&
            i.classSeries === fund.classSeries,
        );
        return { x: point.date, y: item?.rentabilityCdi ?? null };
      }),
    }));
  }, [profitabilityData, monthlyCdiPeriod, uniqueFunds]);

  return {
    handleSelectionsChange,
    noDataFunds,
    isProfitabilityLoading,
    isProfitabilityError,
    profitabilityData,
    uniqueFunds,
    accumulatedSeries,
    accumulatedPeriod,
    setAccumulatedPeriod,
    monthlyReturnsSeries,
    monthlyReturnsPeriod,
    setMonthlyReturnsPeriod,
    monthlyCdiSeries,
    monthlyCdiPeriod,
    setMonthlyCdiPeriod,
  };
}
